import { Box, Typography } from '@mui/material';
import React, { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { LightTooltip } from '../../../../shared/CustomTooltip/CustomTooltip';
import zeplinColor from '../../../../../theme/Color';
import CustomButton from '../../../../shared/CustomButton/CustomButton';
import style from './Integration.module.css';
import CustomTab from '../../../../shared/CustomTab/CustomTab';
import IntegrationCard, { IntegrationConfig } from './IntegrationCard';
import ApiConfirmationModal from './ApiConfirmationModal';
import AddIntegrationModal from './AddIntegrationModal';
import DeleteIntegrationModal from './DeleteIntegrationModal';
import { initialAlertState, useAlertStatus } from '../../../../../customHook/useAlertStatus';
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks';
import {
  addIntegration,
  fetchIntegrations,
  updateIntegration,
} from '../../../../../store/slices/integrationSlice';
import CustomAlert from '../../../../shared/CustomAlert/CustomAlert';
import { AdminService } from '../../../../../service/admin.service';

interface IntegrationState {
  [key: string]: {
    dev: IntegrationConfig;
    prod: IntegrationConfig;
  };
}

const Integration = () => {
  const [value, setValue] = useState(0);
  const [modeValue, setModeValue] = useState(0);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editMode, setEditMode] = useState<'add' | 'edit'>('add');
  const [editingIntegration, setEditingIntegration] = useState<any>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { completeOperation, setAlertState, alertState, getAlertContent } = useAlertStatus();
  const dispatch = useAppDispatch();
  const { integrations, loading, error } = useAppSelector((state) => state.integration);
  const [pendingToggle, setPendingToggle] = useState<{
    mode: 'dev' | 'prod';
    newValue: boolean;
  } | null>(null);
  const [localEdits, setLocalEdits] = useState<IntegrationState>({});

  const integrationConfig = useMemo(() => {
    if (integrations.length === 0) return {};

    const config: IntegrationState = {};
    integrations.forEach((integration) => {
      const key = integration.name.toLowerCase();
      const devBaseConfig = {
        clientId: integration?.dev?.clientId,
        clientSecret: integration?.dev?.clientSecret,
        apiToken: integration?.dev?.apiToken,
        homeUrl: integration?.dev?.homeUrl,

        isEnabled: integration?.dev?.isEnabled,
      };
      const prodBaseConfig = {
        clientId: integration?.prod?.clientId,
        clientSecret: integration?.prod?.clientSecret,
        apiToken: integration?.prod?.apiToken,
        homeUrl: integration?.prod?.homeUrl,

        isEnabled: integration?.prod?.isEnabled,
      };
      config[key] = {
        dev: { ...devBaseConfig },
        prod: { ...prodBaseConfig },
      };
    });
    return config;
  }, [integrations]);

  const handleChange = (_: SyntheticEvent, newValue: number) => {
    sessionStorage.setItem('currentAdminConsoleTab', newValue.toString());
    setValue(newValue);
    setModeValue(0);
  };

  const handleModeChange = (_: SyntheticEvent, newValue: number) => {
    setModeValue(newValue);
  };

  const getTabKey = (tabIndex: number): string => {
    if (integrations[tabIndex]) {
      return integrations[tabIndex].name.toLowerCase();
    }
    return '';
  };

  const getIntegrationName = (tabIndex: number): string => {
    if (integrations[tabIndex]) {
      return integrations[tabIndex].name;
    }
    return '';
  };

  const getMergedConfig = useCallback(
    (mode: 'dev' | 'prod'): IntegrationConfig | undefined => {
      const tabKey = getTabKey(value);
      const baseConfig =
        mode === 'dev' ? integrationConfig[tabKey]?.dev : integrationConfig[tabKey]?.prod;
      const editedConfig = mode === 'dev' ? localEdits[tabKey]?.dev : localEdits[tabKey]?.prod;
      return { ...baseConfig, ...editedConfig };
    },
    [value, integrationConfig, localEdits],
  );

  const hasUnsavedChanges = useMemo(() => {
    const tabKey = getTabKey(value);
    return !!localEdits[tabKey];
  }, [value, localEdits]);

  const handleSaveChanges = () => {
    const currentIntegration = integrations[value];
    if (!currentIntegration) return;

    const tabKey = getTabKey(value);
    const edits = localEdits[tabKey];
    if (!edits) return;

    // Determine current mode (0 = dev, 1 = prod)
    const currentMode = modeValue === 0 ? 'dev' : 'prod';

    // Prepare payload with only the current mode configuration
    const payload = {
      [currentMode]: {
        clientId: edits[currentMode]?.clientId ?? currentIntegration?.[currentMode]?.clientId ?? '',
        apiToken: edits[currentMode]?.apiToken ?? currentIntegration?.[currentMode]?.apiToken ?? '',
        homeUrl: edits[currentMode]?.homeUrl ?? currentIntegration?.[currentMode]?.homeUrl ?? '',
        clientSecret:
          edits[currentMode]?.clientSecret ?? currentIntegration?.[currentMode]?.clientSecret ?? '',
        isEnabled:
          edits[currentMode]?.isEnabled ?? currentIntegration?.[currentMode]?.isEnabled ?? false,
        allowedDomains: currentIntegration?.[currentMode]?.allowedDomains ?? [],
      },
    };

    dispatch(updateIntegration({ integrationId: currentIntegration.integrationId, payload }))
      .unwrap()
      .then(() => {
        // Refresh integrations from API
        return dispatch(fetchIntegrations()).unwrap();
      })
      .then(() => {
        // Clear edits for this tab after successful save
        setLocalEdits((prev) => {
          const updated = { ...prev };
          delete updated[tabKey];
          return updated;
        });
        completeOperation('update', 'Integration updated successfully', false);
        // Tab value is retained automatically
      })
      .catch((error) => {
        completeOperation('update', 'Failed to update integration', true);
      });
  };

  const handleDiscardChanges = () => {
    const tabKey = getTabKey(value);
    setLocalEdits((prev) => {
      const updated = { ...prev };
      delete updated[tabKey];
      return updated;
    });
  };

  const handleConfigChange = (
    mode: 'dev' | 'prod',
    field: keyof IntegrationConfig,
    newValue: any,
  ) => {
    const tabKey = getTabKey(value);
    setLocalEdits((prev) => ({
      ...prev,
      [tabKey]: {
        ...(prev[tabKey] || { dev: {}, prod: {} }),
        [mode]: {
          ...(prev[tabKey]?.[mode] || {}),
          [field]: newValue,
        },
      },
    }));
  };

  const handleToggleEnabled = (mode: 'dev' | 'prod', newValue: boolean) => {
    setPendingToggle({ mode, newValue });
    setConfirmModalOpen(true);
  };

  const handleConfirmToggle = () => {
    if (!pendingToggle) return;

    const currentIntegration = integrations[value];
    if (!currentIntegration) return;

    // Prepare payload with only the toggled mode configuration
    const payload = {
      [pendingToggle.mode]: {
        ...currentIntegration[pendingToggle.mode],
        isEnabled: pendingToggle.newValue,
      },
    };

    dispatch(updateIntegration({ integrationId: currentIntegration.integrationId, payload }))
      .unwrap()
      .then(() => {
        // Refresh integrations from API
        return dispatch(fetchIntegrations()).unwrap();
      })
      .then(() => {
        completeOperation(
          'update',
          `Integration ${pendingToggle.newValue ? 'enabled' : 'disabled'} successfully`,
          false,
        );
        setConfirmModalOpen(false);
        setPendingToggle(null);
      })
      .catch((error) => {
        completeOperation('update', 'Failed to update integration status', true);
        setConfirmModalOpen(false);
        setPendingToggle(null);
      });
  };

  const handleCancelToggle = () => {
    setConfirmModalOpen(false);
    setPendingToggle(null);
  };

  const handleAddIntegration = async (integrationName: string, iconUrl: string | null) => {
    if (editMode === 'edit' && editingIntegration) {
      try {
        // Update integration name if changed
        if (integrationName !== editingIntegration.name) {
          await AdminService.updateIntegration(editingIntegration.integrationId, {
            name: integrationName,
          });
        }

        // Update integration icon if changed
        if (iconUrl && iconUrl !== editingIntegration.iconUrl) {
          await AdminService.editIntegrationIcon(editingIntegration.integrationId, {
            iconUrl: iconUrl,
          });
        }

        await dispatch(fetchIntegrations()).unwrap();
        completeOperation('update', 'Integration updated successfully', false);
        setAddModalOpen(false);
        setEditMode('add');
        setEditingIntegration(null);
      } catch (error) {
        completeOperation('update', 'Failed to update integration', true);
      }
    } else {
      // Add new integration
      dispatch(addIntegration({ name: integrationName, iconUrl }))
        .unwrap()
        .then(() => {
          return dispatch(fetchIntegrations()).unwrap();
        })
        .then(() => {
          completeOperation('create', 'Integration added successfully', false);
          setAddModalOpen(false);
        })
        .catch((error) => {
          completeOperation('create', 'Failed to add integration', true);
        });
    }
  };

  const handleEditClick = () => {
    const currentIntegration = integrations[value];
    if (!currentIntegration) return;

    setEditingIntegration(currentIntegration);
    setEditMode('edit');
    setAddModalOpen(true);
  };

  const handleDeleteIntegration = async () => {
    const currentIntegration = integrations[value];
    if (!currentIntegration) return;

    try {
      setDeleteLoading(true);
      await AdminService.deleteIntegration(currentIntegration.integrationId);
      await dispatch(fetchIntegrations()).unwrap();
      setValue(0);
      completeOperation('delete', 'Integration deleted successfully', false);
      setDeleteModalOpen(false);
    } catch (error) {
      completeOperation('delete', 'Failed to delete integration', true);
    } finally {
      setDeleteLoading(false);
    }
  };

  const currentDevConfig = getMergedConfig('dev');
  const currentProdConfig = getMergedConfig('prod');

  const Tabs = integrations.map((integration, index) => ({
    label: integration.name,
    id: index + 1,
    isHidden: value !== index,
    Component: (
      <IntegrationCard
        title={`${integration.name}`}
        iconUrl={integration.iconUrl}
        modeValue={modeValue}
        onModeChange={handleModeChange}
        devConfig={currentDevConfig}
        prodConfig={currentProdConfig}
        onConfigChange={handleConfigChange}
        onToggleEnabled={handleToggleEnabled}
        onDeleteClick={() => setDeleteModalOpen(true)}
        onEditClick={handleEditClick}
        hasUnsavedChanges={hasUnsavedChanges}
        onSaveChanges={handleSaveChanges}
        onDiscardChanges={handleDiscardChanges}
        loading={loading}
      />
    ),
  }));

  useEffect(() => {
    dispatch(fetchIntegrations())
      .unwrap()
      .catch((error) => {
        completeOperation('fetch', 'Failed to fetch integrations', true);
      });
  }, [dispatch]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='responsiveBody3'>
          Configure and manage authentication details for each connected platform.
          <br /> These settings enable secure communication between Huddle and third-party services.
        </Typography>
        <LightTooltip
          title={
            <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
              New Integration
            </Typography>
          }
          placement='bottom-end'
          style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
        >
          <Box sx={{ width: 'fit-content' }}>
            <CustomButton
              disabled={false}
              onClick={() => {
                setEditMode('add');
                setEditingIntegration(null);
                setAddModalOpen(true);
              }}
              variant='contained'
              color={'primary'}
              buttonVariant='responsiveButtonLarge'
              style={{
                width: '100%',
                borderRadius: { xs: '10px', xl: '14px' },
                height: { xs: 30, xl: 48 },
                padding: { xs: '10px 12px', xl: '14px 16px' },
                '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
              }}
              textStyle={{ color: zeplinColor.Neutral1 }}
              text={'New Integration'}
              iconPosition='start'
              icon={<img src={`/assets/add-white.svg`} className={style['add-icon']} />}
            />
          </Box>
        </LightTooltip>
      </Box>
      <Box sx={{ padding: { xs: '20px 0rem', xl: '24px 0rem' } }}>
        <CustomTab tabs={Tabs} handleChange={handleChange} value={value} />
      </Box>
      <ApiConfirmationModal
        open={confirmModalOpen}
        integrationName={getIntegrationName(value)}
        isEnabling={pendingToggle?.newValue || false}
        onConfirm={handleConfirmToggle}
        onCancel={handleCancelToggle}
        loading={loading}
      />
      <AddIntegrationModal
        isModalOpen={addModalOpen}
        handleClose={() => setAddModalOpen(false)}
        onAdd={handleAddIntegration}
        loading={loading}
        mode={editMode}
        initialData={
          editingIntegration
            ? {
                name: editingIntegration.name,
                iconUrl: editingIntegration.iconUrl,
                integrationId: editingIntegration.integrationId,
              }
            : undefined
        }
      />
      <DeleteIntegrationModal
        open={deleteModalOpen}
        integrationName={getIntegrationName(value)}
        onConfirm={handleDeleteIntegration}
        onCancel={() => setDeleteModalOpen(false)}
        loading={deleteLoading}
      />
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </Box>
  );
};

export default Integration;
